import Vue from 'vue'
import { Module, Action, getModule, Mutation, VuexModule } from 'vuex-module-decorators'
import _ from 'lodash'
import { ApiAgent } from '@/plugins/ApiRequest'
import * as ApiPath from '@/tcw/constant/ApiPath'
import { DEFAULT_KAISHI, DEFAULT_SHURYO, DEFAULT_SHIMEBI } from '@/tcw/constant/TcwConstant'
import { ApiGeneralSinglePayload } from '@/tcw/api/types'
import * as tcw from '@/tcw/utils/TcwService'
import store from '@/store'
import { sessionStore } from '@/store/session/SessionStoreModule'
import { KeyInfo } from '@/components/common/form/types'
import { TCWBadDataStructureError } from '@/plugins/TCWBaseError'
import { IFormStore, Project, UpdatePayload } from '../types'

/**
 * メッセージSTORE
 */
@Module({
  name: 'ProjectStoreModule',
  store,
  namespaced: true,
  dynamic: true
})
export class ProjectStoreModule extends VuexModule implements IFormStore<Project> {
  // 編集データ
  protected editTargetData: Project | null = null
  //   // 編集前データ保持用。新規データの場合はundefinedのまま
  protected origin: Project | null = null

  /****************
   * Getter
   ****************/
  public get ready(): boolean {
    return this.editTargetData !== null
  }
  get isNewRecord(): boolean {
    const id = this.editTargetData?.id
    return id === null || id === undefined
  }
  public get editData(): Project {
    return this.editTargetData ? this.editTargetData : ({} as Project)
  }
  public get isLock(): boolean {
    return false
  }
  public get apiPath(): { [type: string]: string } {
    return ApiPath.Paths.Project
  }
  public get isChanged(): boolean {
    if (this.editTargetData === null) {
      return this.origin !== null
    }
    if (this.origin === null) {
      return this.editTargetData !== null
    }
    const destKeys = Object.keys(this.editTargetData)
    const srcKeys = Object.keys(this.origin)

    if (destKeys.length !== srcKeys.length) {
      return true
    }

    const isChanged = Object.entries(this.editTargetData).some(entry => {
      const [key, value] = entry
      const originVal = Object.getOwnPropertyDescriptor(this.origin, key)
      return value !== originVal?.value
    })

    return isChanged
  }

  /****************
   * Action
   ****************/

  /**
   * 空データ取得
   */
  @Action({ rawError: true })
  public async create(): Promise<void> {
    await this.fetchEmpty()
  }

  /**
   * 空データ取得
   */
  @Action({ rawError: true })
  public async fetchEmpty(): Promise<Project> {
    // API実行
    try {
      const apiAgent = ApiAgent.create(tcw.getApiUrlBase(), sessionStore.sessionToken)
      const response = await apiAgent.call<Project>(this.apiPath.empty, 'GET')

      // デフォルトの開始終了時刻を設定
      response.teijiKaishiJikoku = DEFAULT_KAISHI
      response.teijiShuryoJikoku = DEFAULT_SHURYO

      // デフォルトの締日を設定
      response.shimebi = DEFAULT_SHIMEBI

      // STOREにセットする
      this.setEditTarget(response)

      // 呼び出し側に送る
      return response
    } catch (e) {
      console.error('failure fetch form data', e)
      return Promise.reject(e)
    }
  }

  /**
   * 編集データセット
   * @param id 編集データID
   */
  @Action({ rawError: true })
  public async fetch(): Promise<Project> {
    // API実行
    try {
      const apiAgent = ApiAgent.create(tcw.getApiUrlBase(), sessionStore.sessionToken)
      const response = await apiAgent.call<Project>(this.apiPath.get, 'GET')

      // STOREにセットする
      this.setEditTarget(response)

      // 呼び出し側に送る
      return response
    } catch (e) {
      console.error('failure fetch form data', e)
      return Promise.reject(e)
    }
  }

  /**
   * 編集データサーバ送信
   */
  @Action({ rawError: true })
  public async submit(): Promise<Project> {
    console.log(this.editTargetData)
    // API実行
    try {
      // API実行
      const apiAgent = ApiAgent.create(tcw.getApiUrlBase(), sessionStore.sessionToken)
      const response = await apiAgent.call<Project>(
        this.apiPath.post,
        'POST',
        (this.editTargetData as unknown) as ApiGeneralSinglePayload
      )

      return response
    } catch (e) {
      console.error('failure submit form data', e)
      return Promise.reject(e)
    }
  }

  /**
   * 編集データサーバ送信
   */
  @Action({ rawError: true })
  public async delete(key: KeyInfo): Promise<boolean> {
    // API実行
    const path = ApiPath.buildPath(this.apiPath.delete, key)
    const apiAgent = ApiAgent.create(tcw.getApiUrlBase(), sessionStore.sessionToken)
    await apiAgent.call(path, 'DELETE')
    return Promise.resolve(true)
  }

  @Action({ rawError: true })
  public updateRecord(payload: UpdatePayload): void {
    this.update(payload)
  }

  @Action({ rawError: true })
  public clear(): void {
    this.setEditTarget(null)
  }

  @Action({ rawError: true })
  public reset(): void {
    if (this.origin) {
      const origin = _.cloneDeep(this.origin)
      this.setEditTarget(origin)
    }
  }

  @Action
  public set(projectInfo: Project | null): void {
    if (projectInfo) {
      this.setEditTarget(projectInfo)
      this.setOriginalData(projectInfo)
    } else {
      this.fetchEmpty().then(response => this.setEditTarget(response))
    }
  }

  /****************
   * Mutation
   ****************/
  @Mutation
  public update(payload: UpdatePayload): void {
    if (this.editTargetData === null) {
      throw new TCWBadDataStructureError('データがロードされていません')
    }
    Object.keys(payload).forEach(property => {
      const updateValue = payload[property]
      console.debug(`update record: ${property} => ${updateValue}`)
      if (this.editTargetData != null) {
        // non-null-assertion回避
        Vue.set(this.editTargetData, property, updateValue)
      }
    })
  }
  @Mutation
  protected setEditTarget(record: Project | null): void {
    this.editTargetData = record
  }
  @Mutation
  protected setOriginalData(record: Project | null): void {
    this.origin = _.cloneDeep(record)
  }
}

export const projectStore = getModule(ProjectStoreModule)
